<template>
  <v-card>
    <v-card-text class="pa-4">
      <div class="field-sessions__title">{{ $t('onboarding.field.sessions.title') }}</div>
      <p v-if="!loading && !sessions.length" class="text-body-2 text--secondary mb-0">{{ $t('onboarding.field.sessions.none') }}</p>

      <!-- Une charge par ligne ; on l'ouvre pour voir sa courbe. -->
      <div v-for="session in sessions" :key="session.id" class="field-sessions__row">
        <div class="d-flex align-center" role="button" @click="toggle(session)">
          <div class="flex-grow-1">
            <div class="text-body-2 font-weight-medium">
              {{ session.startedAt ? $options.filters.formatDate(session.startedAt, 'HH:mm') : '-' }}
              <span class="text--secondary">· {{ session.evseId || '-' }}</span>
            </div>
            <div class="text-caption text--secondary">
              {{ energyOf(session) }} · {{ durationOf(session) }}<span v-if="session.badge"> · {{ session.badge }}</span>
            </div>
          </div>
          <v-chip x-small :color="session.status === 'ACTIVE' ? 'success' : undefined" :outlined="session.status !== 'ACTIVE'" :dark="session.status === 'ACTIVE'">
            {{ statusLabel(session.status) }}
          </v-chip>
          <v-icon small class="ml-1">{{ openId === session.id ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </div>

        <div v-if="openId === session.id" class="mt-2">
          <div v-if="!curve" class="text-center py-3"><v-progress-circular indeterminate size="20" width="2" color="primary" /></div>
          <p v-else-if="!curve.metrics.length" class="text-caption text--secondary mb-0">{{ $t('onboarding.field.sessions.no-curve') }}</p>
          <session-metrics-chart v-else :metrics="curve.metrics" :events="curve.events" :start-time="curve.startTime" :stop-time="curve.stopTime" :height="200" />
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import FieldRepository from '@repository/FieldRepository'
import SessionMetricsChart from '@blocks/SessionMetricsChart.vue'

/**
 * Les sessions du jour de la borne (trois au plus) : après une charge d'essai, le technicien voit si la session a été
 * créée. Pas le CDR : au simulateur, la charge fait 0 kWh et son CDR reste bloqué en correction. Une session s'ouvre
 * sur sa courbe (ses relevés), qui se met à jour tant qu'elle est en cours.
 */
export default {
  name: 'FieldSessions',

  components: { SessionMetricsChart },

  props: {
    chargingPointId: { type: String, required: true },
    /** Change quand la borne parle (temps réel ou rafraîchissement) : la liste et la courbe ouverte se relisent. */
    refreshKey: { type: Number, default: 0 },
  },

  data() {
    return { sessions: [], loading: false, openId: null, curve: null }
  },

  watch: {
    refreshKey() {
      this.load()
    },
  },

  created() {
    this.load()
  },

  methods: {
    async load() {
      this.loading = true
      try {
        this.sessions = (await FieldRepository.sessions(this.chargingPointId)).sessions || []
        const open = this.sessions.find(s => s.id === this.openId)
        if (open && (open.status === 'ACTIVE' || !this.curve)) {
          this.loadCurve(open)
        }
      } catch (e) {
        // La fiche dit déjà quand le réseau manque ; la liste reprend au prochain rafraîchissement.
      } finally {
        this.loading = false
      }
    },

    toggle(session) {
      if (this.openId === session.id) {
        this.openId = null
        return
      }
      this.openId = session.id
      this.curve = null
      this.loadCurve(session)
    },

    async loadCurve(session) {
      try {
        const curve = await FieldRepository.sessionCurve(this.chargingPointId, session.id)
        if (this.openId === session.id) {
          this.curve = curve
        }
      } catch (e) {
        this.curve = { metrics: [], events: [] }
      }
    },

    statusLabel(status) {
      const key = `onboarding.field.sessions.status.${status}`
      return this.$te(key) ? this.$t(key) : (status || '-')
    },

    energyOf(session) {
      return `${((session.energyWh || 0) / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })} kWh`
    },

    durationOf(session) {
      const seconds = session.durationSeconds || 0
      return seconds < 60 ? `${seconds} s` : `${Math.round(seconds / 60)} min`
    },
  },
}
</script>

<style scoped>
.field-sessions__title {
  font-weight: 500;
  font-size: 1.05rem;
  margin-bottom: 8px;
}
.field-sessions__row {
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.field-sessions__row:last-child {
  border-bottom: none;
}
</style>
